import { useState, useEffect } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Sidebar } from './Sidebar';
import { Header } from './Header';
import { useAuth } from '../../context/AuthContext';
import { useScrollToTop } from '../../hooks/useScrollToTop';

const pageConfig: Record<string, { title: string; subtitle?: string }> = {
  '/': { title: 'Dashboard', subtitle: 'Overview of your business metrics' },
  '/products': { title: 'Product Management', subtitle: 'Manage your product catalog and inventory' },
  '/bundles': { title: 'Bundle Management', subtitle: 'Create and manage curated bundles and subscription boxes' },
  '/orders': { title: 'Order Management', subtitle: 'Track and manage customer orders' },
  '/customers': { title: 'Customer Management', subtitle: 'Manage customer relationships and profiles' },
  '/users': { title: 'User & Admin Management', subtitle: 'Manage admin accounts and permissions' },
  '/content': { title: 'Content Management', subtitle: 'Manage banners, FAQs, testimonials, and website content' },
  '/marketing': { title: 'Marketing & Promotions', subtitle: 'Manage discount codes, campaigns, and promotional events' },
  '/financial': { title: 'Financial Management', subtitle: 'Revenue reports, payment processing, tax management, and expense tracking' },
  '/inventory': { title: 'Inventory & Logistics', subtitle: 'Stock management, supplier management, shipping rates, and purchase orders' },
  '/analytics': { title: 'Analytics & Reporting', subtitle: 'Performance metrics, real-time dashboard, product analytics, and custom reports' },
  '/affiliates': { title: 'Affiliate Management', subtitle: 'Manage affiliate applications, approvals, and payouts' },
  '/rushd/analytics': { title: 'Rush\'d Analytics', subtitle: 'Track Rush\'d subscription performance and member metrics' },
  '/rushd/settings': { title: 'Rush\'d Settings', subtitle: 'Configure Rush\'d subscription plans, pricing, and benefits' },
  '/settings': { title: 'Settings', subtitle: 'Manage your admin portal configuration and preferences' },
};

export function AdminLayout() {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [headerHeight, setHeaderHeight] = useState(80); // Default header height
  const location = useLocation();
  const { loading } = useAuth();

  // Auto-scroll to top on route changes
  useScrollToTop();

  // Listen for custom events from Header about height changes
  useEffect(() => {
    const handleHeaderHeightChange = (event: CustomEvent) => {
      setHeaderHeight(event.detail.height);
    };

    // Listen for height updates from Header
    window.addEventListener('headerHeightUpdate', handleHeaderHeightChange as EventListener);

    return () => {
      window.removeEventListener('headerHeightUpdate', handleHeaderHeightChange as EventListener);
    };
  }, []);

  // Auto-collapse sidebar on mobile
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 1024) {
        setSidebarCollapsed(true);
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const currentPage = pageConfig[location.pathname] || {
    title: 'Admin Portal',
    subtitle: 'JamPack\'d Administration'
  };

  if (loading) {
    return (
      <div className="min-h-screen relative overflow-hidden flex items-center justify-center">
        {/* Global Beach Background */}
        <div 
          className="fixed inset-0 w-full h-full bg-cover bg-center bg-no-repeat z-0"
          style={{
            backgroundImage: 'url(/beach-background.gif)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        />
        
        {/* White Mask Overlay */}
        <div className="fixed inset-0 bg-white/60 z-0" />
        
        {/* Loading Content */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.3 }}
          className="relative z-10 text-center"
        >
          <div className="w-16 h-16 bg-gradient-to-br from-jamaica-green to-jamaica-green/80 rounded-full flex items-center justify-center mx-auto mb-4">
            <div className="w-8 h-8 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Loading...</h2>
          <p className="text-gray-600">Initializing admin portal</p>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Global Beach Background */}
      <div 
        className="fixed inset-0 w-full h-full bg-cover bg-center bg-no-repeat z-0"
        style={{
          backgroundImage: 'url(/beach-background.gif)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      />
      
      {/* White Mask Overlay */}
      <div className="fixed inset-0 bg-white/60 z-0" />
      
      {/* Content Layer */}
      <div className="relative z-10 min-h-screen">
        {/* Sidebar */}
        <Sidebar
          isCollapsed={sidebarCollapsed}
          onToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
        />

        {/* Main Content */}
        <div 
          className="flex flex-col min-h-screen transition-all duration-300 ease-in-out"
          style={{ marginLeft: sidebarCollapsed ? '80px' : '280px' }}
        >
          {/* Header */}
          <Header
            title={currentPage.title}
            subtitle={currentPage.subtitle}
            sidebarCollapsed={sidebarCollapsed}
          />

          {/* Page Content */}
          <main className="flex-1 overflow-auto" style={{ paddingTop: `${headerHeight}px` }}>
            <motion.div
              key={location.pathname}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3, ease: 'easeInOut' }}
              className="p-6"
            >
              <Outlet />
            </motion.div>
          </main>
        </div>

        {/* Mobile Overlay */}
        {!sidebarCollapsed && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 bg-black bg-opacity-50 z-30 lg:hidden"
            onClick={() => setSidebarCollapsed(true)}
          />
        )}
      </div>
    </div>
  );
} 
